/**
 * Centralized localized strings for components that have their own
 * micro-dictionaries instead of using the global i18n keys.
 *
 * Supported langs: en, ar, id, ur, fr, bn, zh, fa, tr, es, pt, de, it (13 total)
 *
 * Usage:
 *   import { pickLang, RTL_LANGS } from '@/utils/localizedStrings';
 *   const lang = pickLang(i18n.language);   // safe fallback to 'en'
 *   const isRTL = RTL_LANGS.has(lang);
 */

export const SUPPORTED_LANGS = ['en', 'ar', 'id', 'ur', 'fr', 'bn', 'zh', 'fa', 'tr', 'es', 'pt', 'de', 'it'];
export const RTL_LANGS = new Set(['ar', 'ur', 'fa']);

/** Returns lang if supported, else 'en'. */
export const pickLang = (lang) =>
  SUPPORTED_LANGS.includes(lang) ? lang : 'en';

/** Pick a value from a per-lang dict with safe fallback to en, then ar. */
export const tt = (dict, lang) => {
  if (!dict) return '';
  if (dict[lang] !== undefined) return dict[lang];
  if (dict.en !== undefined) return dict.en;
  if (dict.ar !== undefined) return dict.ar;
  return '';
};

// ---- Prayer names (transliterations / native forms) ----
export const PRAYER_NAMES = {
  fajr: {
    en: 'Fajr', ar: 'الفجر', id: 'Subuh', ur: 'فجر',
    fr: 'Fajr', bn: 'ফজর', zh: '晨礼 (法吉尔)', fa: 'فجر', tr: 'İmsak',
    es: 'Fajr', pt: 'Fajr', de: 'Fadschr', it: 'Fajr'
  },
  sunrise: {
    en: 'Sunrise', ar: 'الشروق', id: 'Matahari Terbit', ur: 'طلوع آفتاب',
    fr: 'Lever du soleil', bn: 'সূর্যোদয়', zh: '日出', fa: 'طلوع آفتاب', tr: 'Güneş',
    es: 'Amanecer', pt: 'Nascer do Sol', de: 'Sonnenaufgang', it: 'Alba'
  },
  dhuhr: {
    en: 'Dhuhr', ar: 'الظهر', id: 'Dzuhur', ur: 'ظہر',
    fr: 'Dhuhr', bn: 'যোহর', zh: '晌礼 (祖海尔)', fa: 'ظهر', tr: 'Öğle',
    es: 'Dhuhr', pt: 'Dhuhr', de: 'Dhuhr', it: 'Dhuhr'
  },
  asr: {
    en: 'Asr', ar: 'العصر', id: 'Ashar', ur: 'عصر',
    fr: 'Asr', bn: 'আসর', zh: '晡礼 (阿萨尔)', fa: 'عصر', tr: 'İkindi',
    es: 'Asr', pt: 'Asr', de: 'Asr', it: 'Asr'
  },
  maghrib: {
    en: 'Maghrib', ar: 'المغرب', id: 'Maghrib', ur: 'مغرب',
    fr: 'Maghrib', bn: 'মাগরিব', zh: '昏礼 (玛格里布)', fa: 'مغرب', tr: 'Akşam',
    es: 'Magrib', pt: 'Magreb', de: 'Maghrib', it: 'Maghrib'
  },
  isha: {
    en: 'Isha', ar: 'العشاء', id: 'Isya', ur: 'عشاء',
    fr: 'Isha', bn: 'এশা', zh: '宵礼 (伊沙)', fa: 'عشاء', tr: 'Yatsı',
    es: 'Isha', pt: 'Isha', de: 'Ischa', it: 'Isha'
  },
};

// ---- Generic short labels used by multiple components ----
export const COMMON = {
  today: {
    en: 'Today', ar: 'اليوم', id: 'Hari Ini', ur: 'آج',
    fr: "Aujourd'hui", bn: 'আজ', zh: '今天', fa: 'امروز', tr: 'Bugün',
    es: 'Hoy', pt: 'Hoje', de: 'Heute', it: 'Oggi'
  },
  thisMonth: {
    en: 'Month', ar: 'الشهر', id: 'Bulan', ur: 'ماہ',
    fr: 'Mois', bn: 'মাস', zh: '本月', fa: 'ماه', tr: 'Ay',
    es: 'Mes', pt: 'Mês', de: 'Monat', it: 'Mese'
  },
  days: {
    en: 'days', ar: 'يوماً', id: 'hari', ur: 'دن',
    fr: 'jours', bn: 'দিন', zh: '天', fa: 'روز', tr: 'gün',
    es: 'días', pt: 'dias', de: 'Tage', it: 'giorni'
  },
  AH: {
    en: 'AH', ar: 'هـ', id: 'H', ur: 'ہجری',
    fr: 'AH', bn: 'হিজরি', zh: '希吉来', fa: 'ه‍.ق', tr: 'H.',
    es: 'AH', pt: 'H.', de: 'n. H.', it: 'A.E.'
  },
  min: {
    en: 'Min', ar: 'دقيقة', id: 'Mnt', ur: 'منٹ',
    fr: 'Min', bn: 'মিনিট', zh: '分', fa: 'دقیقه', tr: 'Dk',
    es: 'Min', pt: 'Min', de: 'Min', it: 'Min'
  },
  sec: {
    en: 'Sec', ar: 'ثانية', id: 'Dtk', ur: 'سیکنڈ',
    fr: 'Sec', bn: 'সেকেন্ড', zh: '秒', fa: 'ثانیه', tr: 'Sn',
    es: 'Seg', pt: 'Seg', de: 'Sek', it: 'Sec'
  },
  hijri: {
    en: 'Hijri', ar: 'هجري', id: 'Hijriah', ur: 'ہجری',
    fr: 'Hégire', bn: 'হিজরি', zh: '希吉来', fa: 'هجری', tr: 'Hicri',
    es: 'Hégira', pt: 'Hégira', de: 'Hidschri', it: 'Egira'
  },
  gregorian: {
    en: 'Gregorian', ar: 'ميلادي', id: 'Masehi', ur: 'عیسوی',
    fr: 'Grégorien', bn: 'গ্রেগরিয়ান', zh: '公历', fa: 'میلادی', tr: 'Miladi',
    es: 'Gregoriano', pt: 'Gregoriano', de: 'Gregorianisch', it: 'Gregoriano'
  },
  both: {
    en: 'Both', ar: 'مزدوج', id: 'Keduanya', ur: 'دونوں',
    fr: 'Les deux', bn: 'উভয়', zh: '双重', fa: 'هر دو', tr: 'İkisi',
    es: 'Ambos', pt: 'Ambos', de: 'Beide', it: 'Entrambi'
  },
  whiteDays: {
    en: 'White Days', ar: 'الأيام البيض', id: 'Ayyamul Bidh', ur: 'ایام بیض',
    fr: 'Jours Blancs', bn: 'আইয়ামুল বীজ', zh: '白日斋', fa: 'ایام بیض', tr: 'Eyyâm-ı Bîd',
    es: 'Días Blancos', pt: 'Dias Brancos', de: 'Weiße Tage', it: 'Giorni Bianchi'
  },
  whiteDaysHint: {
    en: 'White Days (recommended fasting)',
    ar: 'من الأيام البيض',
    id: 'Ayyamul Bidh (puasa sunnah)',
    ur: 'ایام بیض (نفل روزے)',
    fr: 'Jours Blancs (jeûne recommandé)',
    bn: 'আইয়ামুল বীজ (নফল রোজা)',
    zh: '白日斋（嘉行斋戒）',
    fa: 'ایام بیض (روزه مستحب)',
    tr: 'Eyyâm-ı Bîd (sünnet oruç)',
    es: 'Días Blancos (ayuno recomendado)',
    pt: 'Dias Brancos (jejum recomendado)',
    de: 'Weiße Tage (empfohlenes Fasten)',
    it: 'Giorni Bianchi (digiuno raccomandato)'
  },
  blessedFriday: {
    en: 'Blessed Friday', ar: 'يوم الجمعة المبارك', id: "Jumat yang Diberkahi",
    ur: 'جمعۃ المبارک', fr: 'Vendredi Béni', bn: 'বরকতময় শুক্রবার',
    zh: '吉庆主麻日', fa: 'جمعه مبارک', tr: 'Mübarek Cuma',
    es: 'Viernes Bendito', pt: 'Sexta-feira Abençoada',
    de: 'Gesegneter Freitag', it: 'Venerdì Benedetto'
  },
  friday: {
    en: 'Friday', ar: 'الجمعة', id: 'Jumat', ur: 'جمعہ',
    fr: 'Vendredi', bn: 'শুক্রবার', zh: '周五', fa: 'جمعه', tr: 'Cuma',
    es: 'Viernes', pt: 'Sexta-feira', de: 'Freitag', it: 'Venerdì'
  },
  eidEvent: {
    en: 'Eid / Event', ar: 'عيد / مناسبة', id: 'Idul / Hari Raya',
    ur: 'عید / تقریب', fr: 'Aïd / Événement', bn: 'ঈদ / উৎসব',
    zh: '开斋节 / 重要日', fa: 'عید / مناسبت', tr: 'Bayram / Etkinlik',
    es: 'Eid / Evento', pt: 'Eid / Evento',
    de: 'Eid / Anlass', it: 'Eid / Evento'
  },
  selectCity: {
    en: 'Select city', ar: 'اختر مدينة', id: 'Pilih kota', ur: 'شہر منتخب کریں',
    fr: 'Sélectionner une ville', bn: 'শহর নির্বাচন করুন', zh: '选择城市',
    fa: 'یک شهر را انتخاب کنید', tr: 'Şehir seç',
    es: 'Seleccionar ciudad', pt: 'Selecionar cidade',
    de: 'Stadt auswählen', it: 'Seleziona città'
  },
  searchCity: {
    en: 'Search city or country...',
    ar: 'ابحث عن مدينة أو بلد...',
    id: 'Cari kota atau negara...',
    ur: 'شہر یا ملک تلاش کریں...',
    fr: 'Rechercher une ville ou un pays...',
    bn: 'শহর বা দেশ অনুসন্ধান করুন...',
    zh: '搜索城市或国家...',
    fa: 'جستجوی شهر یا کشور...',
    tr: 'Şehir veya ülke ara...',
    es: 'Buscar ciudad o país...',
    pt: 'Pesquisar cidade ou país...',
    de: 'Stadt oder Land suchen...',
    it: 'Cerca città o paese...'
  },
  noResults: {
    en: 'No results', ar: 'لا توجد نتائج', id: 'Tidak ada hasil', ur: 'کوئی نتیجہ نہیں',
    fr: 'Aucun résultat', bn: 'কোনো ফলাফল নেই', zh: '没有结果',
    fa: 'هیچ نتیجه‌ای یافت نشد', tr: 'Sonuç yok',
    es: 'Sin resultados', pt: 'Sem resultados',
    de: 'Keine Ergebnisse', it: 'Nessun risultato'
  },
  result: {
    en: 'result', ar: 'نتيجة', id: 'hasil', ur: 'نتیجہ',
    fr: 'résultat', bn: 'ফলাফল', zh: '结果', fa: 'نتیجه', tr: 'sonuç',
    es: 'resultado', pt: 'resultado', de: 'Ergebnis', it: 'risultato'
  },
  results: {
    en: 'results', ar: 'نتائج', id: 'hasil', ur: 'نتائج',
    fr: 'résultats', bn: 'ফলাফল', zh: '结果', fa: 'نتیجه', tr: 'sonuç',
    es: 'resultados', pt: 'resultados', de: 'Ergebnisse', it: 'risultati'
  },
  citiesWorldwide: {
    en: 'cities worldwide', ar: 'مدينة حول العالم', id: 'kota di seluruh dunia',
    ur: 'شہر دنیا بھر میں', fr: 'villes dans le monde', bn: 'বিশ্বব্যাপী শহর',
    zh: '个城市遍布全球', fa: 'شهر در سراسر جهان', tr: 'şehir dünya çapında',
    es: 'ciudades en todo el mundo', pt: 'cidades no mundo',
    de: 'Städte weltweit', it: 'città nel mondo'
  },
  digital: {
    en: 'Digital', ar: 'رقمية', id: 'Digital', ur: 'ڈیجیٹل',
    fr: 'Numérique', bn: 'ডিজিটাল', zh: '数字', fa: 'دیجیتال', tr: 'Dijital',
    es: 'Digital', pt: 'Digital', de: 'Digital', it: 'Digitale'
  },
  analog: {
    en: 'Analog', ar: 'تناظرية', id: 'Analog', ur: 'اینالاگ',
    fr: 'Analogique', bn: 'এনালগ', zh: '指针', fa: 'عقربه‌ای', tr: 'Analog',
    es: 'Analógico', pt: 'Analógico', de: 'Analog', it: 'Analogico'
  },
  syncedCalendars: {
    en: '↕ Synced', ar: '↕ تقويم مقابل', id: '↕ Sinkron', ur: '↕ ہم آہنگ',
    fr: '↕ Synchronisé', bn: '↕ সিঙ্ক্রোনাইজড', zh: '↕ 同步',
    fa: '↕ همگام', tr: '↕ Senkronize',
    es: '↕ Sincronizados', pt: '↕ Sincronizados',
    de: '↕ Synchronisiert', it: '↕ Sincronizzati'
  },
  bigNumHint: {
    en: 'Large number = primary · Small = matching date',
    ar: 'الرقم الكبير = أساسي · الصغير = التاريخ المقابل',
    id: 'Angka besar = utama · Kecil = tanggal pasangan',
    ur: 'بڑا نمبر = اصل · چھوٹا = متعلقہ تاریخ',
    fr: 'Grand nombre = principal · Petit = date correspondante',
    bn: 'বড় সংখ্যা = প্রধান · ছোট = সংশ্লিষ্ট তারিখ',
    zh: '大数字=主要 · 小数字=对应日期',
    fa: 'عدد بزرگ = اصلی · کوچک = تاریخ متناظر',
    tr: 'Büyük sayı = ana · Küçük = karşılık tarih',
    es: 'Número grande = principal · Pequeño = fecha correspondiente',
    pt: 'Número grande = principal · Pequeno = data correspondente',
    de: 'Große Zahl = Haupt · Klein = zugehöriges Datum',
    it: 'Numero grande = principale · Piccolo = data corrispondente'
  },
  hijriSubtitle: {
    en: 'Gregorian day shown below',
    ar: 'بالأيام الميلادية أسفل كل يوم',
    id: 'Hari Masehi ditampilkan di bawah',
    ur: 'عیسوی دن ذیل میں',
    fr: 'Jour grégorien affiché en dessous',
    bn: 'নিচে গ্রেগরিয়ান দিন',
    zh: '下方显示公历日期',
    fa: 'روز میلادی در پایین',
    tr: 'Miladi gün altta gösterilir',
    es: 'Día gregoriano debajo',
    pt: 'Dia gregoriano abaixo',
    de: 'Gregorianischer Tag unten',
    it: 'Giorno gregoriano sotto'
  },
  gregSubtitle: {
    en: 'Hijri day shown below',
    ar: 'بالأيام الهجرية أسفل كل يوم',
    id: 'Hari Hijriah ditampilkan di bawah',
    ur: 'ہجری دن ذیل میں',
    fr: 'Jour hégirien affiché en dessous',
    bn: 'নিচে হিজরি দিন',
    zh: '下方显示希吉来日期',
    fa: 'روز هجری در پایین',
    tr: 'Hicri gün altta gösterilir',
    es: 'Día hégira debajo',
    pt: 'Dia hégira abaixo',
    de: 'Hidschri-Tag unten',
    it: "Giorno dell'Egira sotto"
  },
};

// Short weekday labels (Sun..Sat) per language
export const WEEKDAYS_SHORT = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  ar: ['أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت'],
  id: ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'],
  ur: ['اتوار', 'پیر', 'منگل', 'بدھ', 'جمعرات', 'جمعہ', 'ہفتہ'],
  fa: ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه'],
  tr: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
  fr: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
  bn: ['রবি', 'সোম', 'মঙ্গল', 'বুধ', 'বৃহঃ', 'শুক্র', 'শনি'],
  zh: ['日', '一', '二', '三', '四', '五', '六'],
  es: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
  pt: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  it: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
};

// Moon phase translations.
// Backend returns "<English> (<Arabic>)" e.g. "Full Moon (بدر)". We map by
// the bare English key to produce a localized label per language.
export const MOON_PHASES = {
  'New Moon':         { en: 'New Moon',         ar: 'محاق',          id: 'Bulan Baru',      ur: 'نیا چاند',      fr: 'Nouvelle Lune',    bn: 'অমাবস্যা',         zh: '新月',     fa: 'ماه نو',          tr: 'Yeni Ay',           es: 'Luna Nueva',       pt: 'Lua Nova',           de: 'Neumond',                    it: 'Luna Nuova' },
  'Waxing Crescent':  { en: 'Waxing Crescent',  ar: 'هلال متزايد',   id: 'Sabit Awal',      ur: 'بڑھتا ہلال',     fr: 'Croissant Croissant', bn: 'বৃদ্ধি পাওয়া অর্ধচন্দ্র', zh: '蛾眉月',   fa: 'هلال در حال رشد', tr: 'Büyüyen Hilal',  es: 'Creciente',        pt: 'Crescente',          de: 'Zunehmende Sichel',          it: 'Luna Crescente' },
  'First Quarter':    { en: 'First Quarter',    ar: 'التربيع الأول', id: 'Kuartal Pertama', ur: 'پہلی سہ ماہی',   fr: 'Premier Quartier', bn: 'প্রথম প্রান্তিক',    zh: '上弦月',   fa: 'تربیع اول',       tr: 'İlk Dördün',     es: 'Cuarto Creciente', pt: 'Quarto Crescente',   de: 'Erstes Viertel',             it: 'Primo Quarto' },
  'Waxing Gibbous':   { en: 'Waxing Gibbous',   ar: 'أحدب متزايد',   id: 'Cembung Awal',    ur: 'بڑھتا گھمسان',   fr: 'Gibbeuse Croissante', bn: 'বৃদ্ধি পাওয়া কুঁজোমুখ', zh: '盈凸月',   fa: 'محدب در حال رشد', tr: 'Büyüyen Şişkin Ay', es: 'Gibosa Creciente', pt: 'Gibosa Crescente', de: 'Zunehmender Halbmond',      it: 'Gibbosa Crescente' },
  'Full Moon':        { en: 'Full Moon',        ar: 'بدر',           id: 'Purnama',         ur: 'پورا چاند',      fr: 'Pleine Lune',      bn: 'পূর্ণিমা',          zh: '满月',     fa: 'ماه کامل',        tr: 'Dolunay',        es: 'Luna Llena',       pt: 'Lua Cheia',          de: 'Vollmond',                   it: 'Luna Piena' },
  'Waning Gibbous':   { en: 'Waning Gibbous',   ar: 'أحدب متناقص',   id: 'Cembung Akhir',   ur: 'گھٹتا گھمسان',   fr: 'Gibbeuse Décroissante', bn: 'হ্রাসমান কুঁজোমুখ', zh: '亏凸月',  fa: 'محدب در حال کاهش', tr: 'Küçülen Şişkin Ay', es: 'Gibosa Menguante', pt: 'Gibosa Minguante', de: 'Abnehmender Halbmond',       it: 'Gibbosa Calante' },
  'Last Quarter':     { en: 'Last Quarter',     ar: 'التربيع الأخير',id: 'Kuartal Terakhir',ur: 'آخری سہ ماہی',   fr: 'Dernier Quartier', bn: 'শেষ প্রান্তিক',     zh: '下弦月',   fa: 'تربیع آخر',       tr: 'Son Dördün',     es: 'Cuarto Menguante', pt: 'Quarto Minguante',   de: 'Letztes Viertel',            it: 'Ultimo Quarto' },
  'Waning Crescent':  { en: 'Waning Crescent',  ar: 'هلال متناقص',   id: 'Sabit Akhir',     ur: 'گھٹتا ہلال',     fr: 'Croissant Décroissant', bn: 'হ্রাসমান অর্ধচন্দ্র', zh: '残月',  fa: 'هلال در حال کاهش', tr: 'Küçülen Hilal',  es: 'Menguante',        pt: 'Minguante',          de: 'Abnehmende Sichel',          it: 'Luna Calante' },
};

/**
 * Localize a moon phase label returned by the backend in the form
 *   "Full Moon (بدر)"
 * If we recognize the English portion, return the per-lang translation.
 * Otherwise return the original string unchanged.
 */
export const localizeMoonPhase = (raw, lang) => {
  if (!raw) return '';
  // Strip the Arabic parenthetical to recover the English phase key
  const englishKey = raw.split('(')[0].trim();
  const entry = MOON_PHASES[englishKey];
  if (!entry) return raw;
  return entry[lang] || entry.en;
};
